<template>
  <TransitionRoot
    :show="visible"
    as="template"
  >
    <Dialog @close="$emit('close')">
      <DialogOverlay :visible="visible" />

      <TransitionChild
        as="template"
        enter="duration-200 ease-out"
        enter-from="translate-y-full"
        enter-to="motion-reduce:transition-none translate-y-0"
        leave="duration-200 ease-in"
        leave-from="translate-y-0"
        leave-to="motion-reduce:transition-none translate-y-full"
      >
        <DialogPanel
          ref="panelRef"
          class="fixed inset-x-0 bottom-0 z-20 flex flex-col bg-white rounded-t-[24px] drop-shadow-top overflow-hidden sm:max-w-md sm:mx-auto !max-h-[90dvh]"
          :style="panelStyle"
        >
          <div
            class="absolute inset-x-0 top-0 z-10 h-8 flex justify-center items-start pt-[10px] touch-none select-none cursor-grab active:cursor-grabbing bg-white"
            @pointerdown="onDragStart"
          >
            <span class="h-[3px] w-[108px] rounded-full bg-neutral-400" />
          </div>

          <button
            type="button"
            aria-label="Close"
            class="absolute right-0 top-0 z-20 flex size-[70px] items-start justify-end p-[10px]"
            @click="$emit('close')"
          >
            <span class="flex size-[36px] items-center justify-center rounded-full bg-white border border-neutral-200 shadow-[0_0_1.5px_rgba(0,0,0,0.2)]">
              <svg-icon
                name="close"
                class="w-[22px] h-[22px] text-neutral-900"
              />
            </span>
          </button>

          <div class="grow overflow-y-auto overscroll-contain pt-[52px]">
            <slot />
          </div>
        </DialogPanel>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel
} from '@headlessui/vue'
import { DialogOverlay } from '@oatsovernight/storybook'

// Distance/velocity thresholds that decide whether a released drag
// dismisses the drawer or snaps back to open (mirrors native sheet feel).
const CLOSE_DISTANCE_RATIO = 0.25
const CLOSE_VELOCITY = 0.5 // px/ms
const SETTLE_DURATION = 200 // ms, matches the enter/leave transition above

const props = withDefaults(defineProps<{
  visible: boolean
  maxHeight?: string
}>(), {
  maxHeight: '80dvh'
})

const emit = defineEmits<{
  close: []
}>()

const panelRef = ref<{ $el: HTMLElement } | null>(null)

const hasInteracted = ref(false)
const isDragging = ref(false)
const dragOffset = ref(0)

let startY = 0
let startTime = 0
let moved = false
let panelHeight = 0

const panelStyle = computed(() => {
  const style: Record<string, string> = { maxHeight: props.maxHeight }
  if (hasInteracted.value) {
    style.transform = `translateY(${dragOffset.value}px)`
    style.transition = isDragging.value ? 'none' : `transform ${SETTLE_DURATION}ms ease-out`
  }
  return style
})

const onDragStart = (e: PointerEvent) => {
  hasInteracted.value = true
  isDragging.value = true
  moved = false
  startY = e.clientY
  startTime = Date.now()
  panelHeight = panelRef.value?.$el?.offsetHeight ?? 0

  window.addEventListener('pointermove', onDragMove)
  window.addEventListener('pointerup', onDragEnd, { once: true })
}

const onDragMove = (e: PointerEvent) => {
  const delta = e.clientY - startY
  if (Math.abs(delta) > 4) moved = true
  // Small rubber-band resistance when dragging up past the open position.
  dragOffset.value = delta < 0 ? Math.max(delta * 0.2, -20) : delta
}

const onDragEnd = () => {
  window.removeEventListener('pointermove', onDragMove)
  isDragging.value = false

  if (!moved) {
    // A plain tap (no drag) on the handle/header also dismisses. Hand
    // control straight back to the dialog's own transition instead of
    // going through the drag transform, since there's nothing to settle.
    hasInteracted.value = false
    emit('close')
    return
  }

  const elapsed = Math.max(Date.now() - startTime, 1)
  const velocity = dragOffset.value / elapsed
  const shouldClose = dragOffset.value > panelHeight * CLOSE_DISTANCE_RATIO || velocity > CLOSE_VELOCITY

  if (shouldClose) {
    dragOffset.value = panelHeight
    // Keep driving the transform ourselves through the close: the dialog's
    // own leave transition starts from its "open" position, so handing
    // control back to it here (e.g. via a `visible` watcher) would snap the
    // panel back open for a frame before it slides shut again.
    window.setTimeout(() => emit('close'), SETTLE_DURATION)
  } else {
    dragOffset.value = 0
    // Once the snap-back-to-open settle finishes, the drag transform and
    // the dialog's natural (untransformed) open position agree, so it's
    // safe to hand control back to the dialog's own transition.
    window.setTimeout(() => {
      hasInteracted.value = false 
    }, SETTLE_DURATION)
  }
}

watch(() => props.visible, (visible) => {
  // Guards against an external close (e.g. a route change) firing mid-drag,
  // when there's no drag transform to keep in control of the close.
  if (!visible && isDragging.value) {
    window.removeEventListener('pointermove', onDragMove)
    hasInteracted.value = false
    isDragging.value = false
    dragOffset.value = 0
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onDragMove)
})
</script>
